import React, { useState } from 'react';
import { Eye, User, Mail, Phone, MessageSquare, Calendar, Hash } from 'lucide-react';
import { useSupabase } from '../../hooks/useSupabase';
import { abvigAPI } from '../../services/api';
import DataTable from '../../components/Tables/DataTable';
import Modal from '../../components/UI/Modal';
import { formatDate, getStatusColor, getStatusLabel, truncateText } from '../../utils/helpers';

export default function ContactMessages() {
  const { data } = useSupabase(abvigAPI.getContactMessages);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleView = (item) => {
    setSelectedMessage(item);
    setIsModalOpen(true);
  };

  const renderMessageDetails = (message) => {
    if (!message) return null;

    return (
      <div className="space-y-6">
        {/* Basic Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <User className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Full Name</p>
              <p className="text-sm font-medium text-gray-900">{message.full_name || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm font-medium text-gray-900">{message.email || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-sm font-medium text-gray-900">{message.phone || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Subject</p>
              <p className="text-sm font-medium text-gray-900">{message.subject || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Received</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(message.created_at)}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-400">#</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <span className={`badge ${getStatusColor(message.status)} mt-1`}>
                {getStatusLabel(message.status)}
              </span>
            </div>
          </div>
        </div>

        {/* Message Section */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Message
          </h4>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{message.message || 'No message content'}</p>
          </div>
        </div>

        {/* Status info */}
        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center">
            Status: <span className={`font-semibold ${message.status === 'resolved' ? 'text-green-600' : message.status === 'rejected' ? 'text-red-600' : 'text-yellow-600'}`}>
              {getStatusLabel(message.status)}
            </span>
          </p>
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'full_name', label: 'Full Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone' },
    { key: 'subject', label: 'Subject' },
    { 
      key: 'message', 
      label: 'Message',
      render: (value) => truncateText(value, 30)
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Received',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending', 'resolved', 'rejected']
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
        <p className="text-gray-600">View contact form submissions</p>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} messages`}
        actions={actions}
        searchPlaceholder="Search messages..."
        onRowClick={(item) => handleView(item)}
      />

      {/* Message Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMessage(null);
        }}
        title="Message Details"
        size="max-w-2xl"
      >
        {renderMessageDetails(selectedMessage)}
      </Modal>
    </div>
  );
}